/**
 * Wave 3.2 / D22 — which list results surface Browse Home should mount.
 * Chips (`entity`) + `q` + Seznam `listKind` drive data source, not chrome alone.
 */

import type {
  DiscoveryEntity,
  DiscoveryListKind,
  DiscoverySearchParamsState,
  DiscoveryView,
} from '../discovery/discoverySearchParams.js';

export type BrowseListResultsMode =
  /** Default Screen-1 nearby SP cards from discovery offers. */
  | 'nearby_sales_points'
  /** Empty-q `entity=shop` — paginated `customer-sales-points`. */
  | 'shop_directory_paginated'
  /** With-q `entity=shop` — drain + client-filter (no name API). */
  | 'shop_directory_filtered'
  /** `entity=tenant` or Seznam `listKind=tenant` (empty-q primary Tenanti). */
  | 'tenant_directory'
  /** With-q `entity=all` — Produkty | Obchody | Tenanti sections with counts. */
  | 'vse_three_sections'
  /** `entity=product` (with-q) or flat discovery offers. */
  | 'product_offers'
  /** Empty-q `entity=product` — guidance only (no Vše compose). */
  | 'product_empty_guidance'
  /** Not the list results branch (map / favorites / bag / inactive). */
  | 'none';

export type BrowseListResultsRoutingInput = {
  readonly view: DiscoveryView;
  readonly q: string;
  readonly entity: DiscoveryEntity;
  readonly listKind: DiscoveryListKind;
  /** True when nearby SP groups from discovery are available to render. */
  readonly hasNearbySalesPointGroups: boolean;
};

/**
 * Resolve list-results mode for G3/G4 entity matrix (FR-04 / FR-11 / AC-03 / D22).
 */
export function resolveBrowseListResultsMode(
  input: BrowseListResultsRoutingInput,
): BrowseListResultsMode {
  if (input.view !== 'list') {
    return 'none';
  }

  const q = input.q.trim();
  const hasQ = q.length > 0;
  const { entity, listKind } = input;

  if (!hasQ && entity === 'shop') {
    return 'shop_directory_paginated';
  }
  if (hasQ && entity === 'shop') {
    return 'shop_directory_filtered';
  }
  if (entity === 'tenant') {
    return 'tenant_directory';
  }
  if (!hasQ && entity === 'all' && listKind === 'tenant') {
    return 'tenant_directory';
  }
  if (hasQ && entity === 'all') {
    return 'vse_three_sections';
  }
  if (!hasQ && entity === 'product') {
    return 'product_empty_guidance';
  }
  if (entity === 'product') {
    return 'product_offers';
  }

  // entity=all, empty q, listKind=salesPoint — nearby or flat discovery offers
  if (!hasQ && entity === 'all' && listKind === 'salesPoint') {
    if (input.hasNearbySalesPointGroups) {
      return 'nearby_sales_points';
    }
    return 'product_offers';
  }

  return 'product_offers';
}

/** Whether Browse should fetch paginated public SP directory (empty-q Obchody). */
export function browseNeedsShopDirectoryPaginated(
  state: Pick<DiscoverySearchParamsState, 'view' | 'q' | 'entity'>,
): boolean {
  return (
    state.view === 'list' &&
    state.entity === 'shop' &&
    state.q.trim().length === 0
  );
}

/** Whether Browse should drain SP directory for client-filter (with-q shop / Vše Obchody). */
export function browseNeedsShopDirectoryDrain(
  state: Pick<DiscoverySearchParamsState, 'view' | 'q' | 'entity'>,
): boolean {
  if (state.view !== 'list') {
    return false;
  }
  const hasQ = state.q.trim().length > 0;
  if (!hasQ) {
    return false;
  }
  return state.entity === 'shop' || state.entity === 'all';
}

/** Whether Browse should load public customer-tenants. */
export function browseNeedsTenantDirectory(
  state: Pick<DiscoverySearchParamsState, 'view' | 'q' | 'entity' | 'listKind'>,
): boolean {
  if (state.view !== 'list') {
    return false;
  }
  if (state.entity === 'tenant') {
    return true;
  }
  const hasQ = state.q.trim().length > 0;
  if (hasQ && state.entity === 'all') {
    return true;
  }
  return !hasQ && state.entity === 'all' && state.listKind === 'tenant';
}

/** Whether discovery offer search drives the primary list results. */
export function browseNeedsDiscoveryOfferResults(
  state: Pick<DiscoverySearchParamsState, 'view' | 'q' | 'entity' | 'listKind'>,
): boolean {
  if (state.view !== 'list') {
    return false;
  }
  const mode = resolveBrowseListResultsMode({
    view: state.view,
    q: state.q,
    entity: state.entity,
    listKind: state.listKind,
    hasNearbySalesPointGroups: false,
  });
  return (
    mode === 'product_offers' ||
    mode === 'vse_three_sections' ||
    mode === 'nearby_sales_points'
  );
}
